import { OneApprovalHistory } from '@factorialco/f0-react/dist/experimental'
import {
  AccessesWithTerminationDocument,
  ApprovalsMaterializedApprovalFlowHistoryFragment,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'

import {
  buildApprovers,
  buildMaterializedApprovers,
  buildNewStepStatus,
  buildStepStatus,
} from 'modules/approvals/lib/materializedApprovalFlows'
import Loading from 'modules/layout/components/Page/Loading'

import { OverrideApproverDecision } from './OverrideApproverDesicion'
import { Step } from './Step'

interface ContentProps {
  materializedApprovalFlow: ApprovalsMaterializedApprovalFlowHistoryFragment
  newLook?: boolean
}

const isFinalFlowStatus = (status: ApprovalsMaterializedApprovalFlowHistoryFragment['status']) => {
  return status === 'approved' || status === 'rejected'
}

export const Steps = ({ materializedApprovalFlow, newLook }: ContentProps) => {
  const { data, error } = useQuery(AccessesWithTerminationDocument, {
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    variables: { ids: [materializedApprovalFlow.overrideApproverId!] },
    skip: !materializedApprovalFlow.overrideApproverId,
  })

  const loading = !data && !error && !!materializedApprovalFlow.overrideApproverId
  const overrideApprover = data?.apiCore.accessesConnection.nodes[0]

  const findMaterializedStep = (stepId: number) => {
    return materializedApprovalFlow.steps.find((step) => step.approvalStep?.id === stepId)
  }

  const materializedSteps = materializedApprovalFlow.steps

  const configuredSteps = materializedApprovalFlow.approvalFlow?.steps || []

  const steps = (
    isFinalFlowStatus(materializedApprovalFlow.status) ? materializedSteps : configuredSteps
  ).map((step, index) => {
    const materializedStep =
      step.__typename == 'ApprovalsMaterializedApprovalStep' ? step : findMaterializedStep(step.id)

    const approvers = materializedStep
      ? buildMaterializedApprovers(materializedStep)
      : step.__typename == 'ApprovalsApprovalStep'
      ? buildApprovers(step)
      : []
    const status = buildStepStatus(approvers)
    const newStatus = buildNewStepStatus(approvers)

    const updatedAt =
      status === 'pending' ? null : approvers.find((approver) => approver.updatedAt)?.updatedAt
    return {
      index,
      status,
      newStatus,
      approvers,
      materializedStepId: materializedStep?.id,
      updatedAt: updatedAt,
    }
  })

  const currentStepIndex = steps.find(
    (level) => level.status === 'pending' || level.status === 'changes_requested'
  )?.index

  if (newLook) {
    const newSteps = steps.map((step) => ({
      status: step.newStatus,
      approvers: step.approvers.map((approver) => ({
        firstName: approver.firstName || approver.role,
        lastName: approver.lastName,
        avatar: approver.avatar,
        status: approver.newStatus,
      })),
    }))

    return <OneApprovalHistory steps={newSteps} />
  }

  return (
    <>
      {steps.map((level) => {
        return (
          <Step
            key={level.index}
            index={level.index}
            status={level.status}
            isCurrentStep={level.index === currentStepIndex}
            updatedAt={level.updatedAt}
            overrided={Boolean(materializedApprovalFlow.overrideApproverId)}
            approvers={level.approvers}
          />
        )
      })}
      {loading && <Loading />}
      {!loading && overrideApprover && (
        <OverrideApproverDecision
          overrideApprover={overrideApprover}
          status={materializedApprovalFlow.status}
        />
      )}
    </>
  )
}
